"use client";
import React, { useState } from "react";
import assetLoader from "@utils/internalImageLoder";
import styles from "./ImproveMileage.module.scss";
import { ImproveMileageProps, ModalContentProps } from "./ImproveMileage.types";
import Text from "@components/atoms/Text";
import Image from "next/image";
import Button from "@components/atoms/Button";
import ModalManager from "../ModalManager";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";

interface ImproveMileageClientWrapperProps {
  improveContent: ImproveMileageProps["improveContent"];
  makeName?: string;
  modelName?: string;
  pageType?: string;
  carAvailability?: string;
  cityName?: string;
}

export default function ImproveMileageClientWrapper({
  improveContent,
  makeName,
  modelName,
  pageType,
  carAvailability,
  cityName,
}: ImproveMileageClientWrapperProps) {
  const [isModalOpen, setIsModalOpen] = useState<ModalContentProps | null>(
    null
  );

  // Event tracking for see more button click
  const handleSeeMoreClick = async (
    icon: string,
    cardTitle: string,
    cardContent: string
  ) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.DIMENSIONS_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_see_more",
        card_title: cardTitle,
        vehicle_make: makeName,
        vehicle_model: modelName,
        car_availability: carAvailability,
        city_name: cityName,
      },
    });

    setIsModalOpen({
      icon: icon,
      title: cardTitle,
      content: cardContent,
    });
  };

  // Event tracking for modal close
  const handleModalClose = async () => {
    if (isModalOpen) {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType || PAGE_TYPES.DIMENSIONS_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_close_modal",
          modal_title: isModalOpen.title,
          vehicle_make: makeName,
          vehicle_model: modelName,
          car_availability: carAvailability,
          city_name: cityName,
        },
      });
    }
    setIsModalOpen(null);
  };

  return (
    <>
      {/* Interactive cards with "see more" buttons */}
      <div className={styles.grid}>
        {improveContent.cards.map((card, index) => {
          const truncated = card.content.slice(0, 110).trim();
          return (
            <div key={index} className={styles.card}>
              <Image
                src={`${assetLoader()}/icons/${card.image.src}`}
                alt={`${card.cardTitle}`}
                loading="lazy"
                width={40}
                height={40}
              />
              <div className={styles.cardContent}>
                {card.cardTitle && (
                  <Text className={styles.cardTitle}>{card.cardTitle}</Text>
                )}
                <Text
                  className={`${styles.cardText} ${
                    card.cardTitle && styles.cardTextWithTitle
                  }`}
                >
                  {truncated}
                  {"… "}
                  <Button
                    className={styles.seeMore}
                    onClick={() =>
                      handleSeeMoreClick(
                        `${assetLoader()}/icons/${card.image.src}`,
                        card.cardTitle || "",
                        card.content
                      )
                    }
                  >
                    see more
                  </Button>
                </Text>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal for displaying full content */}
      <ModalManager
        modalHeaderClass={styles.modalHeader}
        customClass={styles.modalBody}
        isOpen={!!isModalOpen}
        onClose={handleModalClose}
        title={
          <div className={styles.modalTitleWrapper}>
            <Image
              src={`${isModalOpen?.icon}`}
              alt="Improve Mileage"
              width={48}
              height={48}
              className={styles.modalTitleIcon}
            />
          </div>
        }
      >
        <div className={styles.modalContentWrapper}>
          <Text className={styles.modalTitle}>{isModalOpen?.title}</Text>
          <Text className={styles.modalContent}>{isModalOpen?.content}</Text>
          <Button className={styles.modalButton} onClick={handleModalClose}>
            Understood
          </Button>
        </div>
      </ModalManager>
    </>
  );
}
